<template>
  <div>
    <div v-if="loading" class="flex items-center justify-center py-4">
      <LoadingComponent size="small" :display-label="false" />
    </div>

    <div v-else-if="localDossier" class="flex flex-col gap-3">
      <!-- Carte : défunt + statut -->
      <div
        class="grid grid-cols-2 divide-x rounded-lg border dark:divide-slate-600 dark:border-slate-600"
      >
        <div class="p-3">
          <div
            class="capitalize-first mb-0.5 text-xs font-semibold tracking-wide text-gray-500 uppercase dark:text-gray-400"
          >
            {{
              concernedPerson?.is_deceased
                ? $t('global.deceased')
                : $t('global.beneficiary')
            }}
          </div>
          <div class="font-bold">
            {{ concernedPerson?.full_name }}
          </div>
          <div
            v-if="concernedPerson?.usual_name"
            class="text-xs text-gray-500 dark:text-gray-400"
          >
            {{ concernedPerson.usual_name }}
          </div>
        </div>
        <div v-if="localDossier.status" class="p-3">
          <div
            class="capitalize-first mb-0.5 text-xs font-semibold tracking-wide text-gray-500 uppercase dark:text-gray-400"
          >
            {{ $t('attributes.status') }}
          </div>
          <div class="text-sm font-medium">
            {{ $t(`enums.dossier.status.${localDossier.status}`) }}
          </div>
        </div>
      </div>

      <!-- Carte : type funérailles | handler -->
      <div
        class="grid grid-cols-2 divide-x rounded-lg border dark:divide-slate-600 dark:border-slate-600"
      >
        <div class="p-3">
          <div
            class="capitalize-first mb-0.5 text-xs font-semibold tracking-wide text-gray-500 uppercase dark:text-gray-400"
          >
            {{ $t('attributes.funerailles_type') }}
          </div>
          <div class="flex items-center gap-1.5 text-sm font-medium">
            <template v-if="funeralType">
              <FontAwesomeIcon
                :icon="['fal', isCremation ? 'flame' : 'coffin']"
                class="text-gray-400"
              />
              <span class="capitalize-first">{{ funeralType }}</span>
            </template>
            <span v-else class="text-gray-400">—</span>
          </div>
        </div>

        <div v-if="handler" class="p-3">
          <div
            class="capitalize-first mb-0.5 text-xs font-semibold tracking-wide text-gray-500 uppercase dark:text-gray-400"
          >
            {{ $t('global.handler') }}
          </div>
          <div class="flex flex-col gap-1">
            <div class="text-sm font-medium">
              {{ handler.usual_name ?? handler.first_name }}
              {{ handler.last_name }}
              <span
                v-if="handler.relationship"
                class="font-normal text-gray-500 dark:text-gray-400"
              >
                ({{ $t(`enums.person.relationship.${handler.relationship}`) }})
              </span>
            </div>
            <a
              v-if="handler.phone_field"
              :href="`tel:${handler.phone_field}`"
              class="flex items-center gap-1.5 text-xs hover:text-blue-600"
            >
              <FontAwesomeIcon
                :icon="['fal', 'phone']"
                class="w-3 shrink-0 text-gray-400"
              />
              {{ handler.phone_field }}
            </a>
            <a
              v-if="handler.mobile_field"
              :href="`tel:${handler.mobile_field}`"
              class="flex items-center gap-1.5 text-xs hover:text-blue-600"
            >
              <FontAwesomeIcon
                :icon="['fal', 'mobile']"
                class="w-3 shrink-0 text-gray-400"
              />
              {{ handler.mobile_field }}
            </a>
            <a
              v-if="handler.email"
              :href="`mailto:${handler.email}`"
              class="flex items-center gap-1.5 text-xs hover:text-blue-600"
            >
              <FontAwesomeIcon
                :icon="['fal', 'envelope']"
                class="w-3 shrink-0 text-gray-400"
              />
              <span class="truncate">{{ handler.email }}</span>
            </a>
          </div>
        </div>
      </div>

      <!-- Décès (si défunt) -->
      <div
        v-if="
          concernedPerson?.is_deceased &&
          (concernedPerson.death_date_time ||
            concernedPerson.place_of_death?.city)
        "
        class="rounded-lg border p-3 dark:border-slate-600"
      >
        <div
          class="capitalize-first mb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase dark:text-gray-400"
        >
          {{ $t('global.death') }}
        </div>
        <div class="flex flex-col gap-0.5 text-sm">
          <div
            v-if="concernedPerson.death_date_time"
            class="flex items-center gap-1.5 text-gray-600 dark:text-gray-300"
          >
            <FontAwesomeIcon
              :icon="['fal', 'calendar']"
              class="shrink-0 text-gray-400"
            />
            {{
              $h.formatDate(
                concernedPerson.death_date_time,
                'DD/MM/YYYY HH:mm'
              )
            }}
          </div>
          <div
            v-if="concernedPerson.place_of_death?.city"
            class="flex items-center gap-1.5 text-gray-600 dark:text-gray-300"
          >
            <FontAwesomeIcon
              :icon="['fal', 'map-marker-alt']"
              class="shrink-0 text-gray-400"
            />
            {{ placeOfDeath }}
          </div>
        </div>
      </div>

      <!-- Tous les événements dans un seul collapse -->
      <BaseDisclosure
        v-if="localDossier.events?.length"
        :title="$t('global.events')"
      >
        <div class="flex flex-col divide-y dark:divide-slate-600">
          <div
            v-for="evt in localDossier.events"
            :key="evt.id"
            class="flex flex-col gap-0.5 py-2 text-sm first:pt-0 last:pb-0"
          >
            <div class="capitalize-first font-semibold">
              {{ $t(`enums.event.type.${evt.type}`) }}
            </div>
            <div
              v-if="evt.date_from"
              class="flex items-center gap-1.5 text-gray-600 dark:text-gray-300"
            >
              <FontAwesomeIcon
                :icon="['fal', 'calendar']"
                class="shrink-0 text-gray-400"
              />
              {{ $h.rangeDateAsString(evt.date_from, evt.date_to) }}
            </div>
            <div
              v-if="evt.place_model?.name"
              class="flex items-center gap-1.5 text-gray-600 dark:text-gray-300"
            >
              <FontAwesomeIcon
                :icon="['fal', 'map-marker-alt']"
                class="shrink-0 text-gray-400"
              />
              {{ evt.place_model.name }}
              <span v-if="evt.place_model.city" class="text-gray-500">
                – {{ evt.place_model.city }}
              </span>
            </div>
          </div>
        </div>
      </BaseDisclosure>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import BaseDisclosure from '@c/addf-package/components/disclosure/BaseDisclosure.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import LoadingComponent from '@c/TheLoadingComponent.vue'

export default {
  name: 'DossierConsultContent',
  components: { BaseDisclosure, FontAwesomeIcon, LoadingComponent },
  props: {
    dossierId: {
      type: [String, Number],
      required: true
    }
  },
  emits: ['loaded'],
  data() {
    return {
      localDossier: null,
      loading: false
    }
  },
  computed: {
    concernedPerson() {
      return this.localDossier?.concernedPerson ?? null
    },
    handler() {
      return this.localDossier?.contacts?.find((c) => c.is_handler) ?? null
    },
    isCremation() {
      return (
        this.localDossier?.events?.some((evt) => evt.type === 'CRE') ?? false
      )
    },
    funeralType() {
      if (this.isCremation) return this.$t('enums.event.type.CRE')
      const hasBur = this.localDossier?.events?.some((evt) => evt.type === 'BUR')
      return hasBur ? this.$t('enums.event.type.BUR') : null
    },
    placeOfDeath() {
      const place = this.concernedPerson?.place_of_death
      if (!place?.city) return null
      return place.name ? `${place.name} - ${place.city}` : place.city
    }
  },
  watch: {
    dossierId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchDossierData()
        }
      }
    }
  },
  methods: {
    ...mapActions({
      processFetchDossier: 'dossier/fetchDossier'
    }),
    fetchDossierData() {
      this.loading = true
      this.localDossier = null
      this.processFetchDossier(this.dossierId)
        .then((response) => {
          this.localDossier = response.dossier ?? null
          this.$emit('loaded', this.localDossier)
        })
        .catch(() => {
          this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>